import type { ButtonHTMLAttributes, ReactNode } from 'react';

type Variant = 'primary' | 'ghost' | 'quiet' | 'danger';

/**
 * Spec 230 — Kridt's button. Four variants and no more.
 *
 * `primary` is the INK BLOCK, not the action colour: the one action a screen
 * exists for, the darkest thing on it, at most one per view. The action colour
 * is for links, focus rings and selection. Gate 230.A.3 asserts no .hub-btn
 * uses the action colour as a fill, because "make it stand out" is exactly the
 * instinct that breaks this.
 *
 * `className` is for what .hub-btn does not set — width, margin, alignment,
 * order — and never for colour or shape. This used to be enforced by the
 * cascade: primitives.css was unlayered, so `.hub-btn` beat every utility.
 * Spec 246 moved it into `@layer components` (the review's cascade-layer
 * rule), and from then on a `className="bg-red-50 rounded-md"` DOES repaint
 * the button. The protection now lives in gate 249.A.2, which renders every
 * route and fails on a button that is not a pill or is filled with the action
 * colour. If a button needs to be quiet inside a toned banner, that is
 * `quiet`, not a tinted primary.
 *
 * Spec 249 (review P1-3): a disabled action says why. Pass
 * `aria-disabled="true"` rather than `disabled` when the reason is shown next
 * to it: the button stays focusable, keeps its style at reduced opacity, and
 * a click does nothing.
 */
export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  round?: boolean;
  children?: ReactNode;
}

export function Button({ variant = 'ghost', round, className, children, onClick, ...rest }: ButtonProps) {
  const inert = rest['aria-disabled'] === true || rest['aria-disabled'] === 'true';
  const cls = ['hub-btn', `hub-btn--${variant}`, round ? 'hub-btn--round' : '', className ?? '']
    .filter(Boolean)
    .join(' ');
  return (
    <button type="button" className={cls} onClick={inert ? (e) => e.preventDefault() : onClick} {...rest}>
      {children}
    </button>
  );
}
